import { Link } from "north.js";
import React from "react";

import { HARDWARE_FEEDBACK_FORM_URL } from "src/shared/constants/hardware";

type THardwareFaq = {
  question: string;
  answer: React.ReactNode;
};

export const HARDWARE_FAQ: THardwareFaq[] = [
  {
    question: "Can someone else pick up my hardware for me?",
    answer:
      "No — the person whose name is on the hardware request must be the one to pick it up. When you check out hardware, we’ll hold onto your ID until all of the items signed out under your name have been returned.",
  },
  {
    question: "Do I need to request everything through the hardware tool?",
    answer: (
      <>
        All <b>standard</b> and <b>special</b> items must be requested through
        the hardware tool. Items marked <b>free</b> don’t need to be requested —
        you can simply pick them up directly from the QNX Makerspace while
        supplies last.
      </>
    ),
  },
  {
    question: "How do special items work?",
    answer: (
      <>
        In order to be selected for a special item, you’ll need to present a
        short, live pitch in the QNX Makerspace about what you’re thinking of
        building with it! Your pitch can be as simple or creative as you’d like,
        but we’ll be awarding points for originality in how you present{" "}
        <span role="img" aria-label="eyes">
          👀
        </span>
        <br />
        <br />
        Pitches will begin at <b>1:00 AM on Saturday</b> in the Makerspace, with
        requests closing at <b>12:50 AM</b>.
      </>
    ),
  },
  {
    question: "What should I do if my hardware request is canceled or denied?",
    answer:
      "The most common reasons are limited availability or a request containing an unusually large number of items. If your request is canceled or denied and you’d like more information, come see us at the QNX Makerspace and we’ll be happy to help.",
  },
  {
    question: "When will my order be ready?",
    answer:
      "You’ll receive an email when your request has been approved, followed by another email when it’s ready for pickup. We’ll do our best to pack requests as quickly as possible, but wait times may be longer during busy periods.",
  },
  {
    question: "When do I need to return my hardware?",
    answer: (
      <>
        All hardware must be returned to the QNX Makerspace by{" "}
        <b>2:00 PM on Sunday</b>. If you’re a Hack the North finalist, you can
        return your hardware after the closing ceremonies.
      </>
    ),
  },
  {
    question: "What happens if I accidentally damage something I borrowed?",
    answer:
      "Don’t worry — you’ll still get your ID back. If an item is defective or you think it may have been damaged, please bring it to the hardware checkout desk and let us know. Replacements may be available, and reporting broken hardware helps us make sure it isn’t accidentally given to another hacker. You won’t be charged for accidental damage unless the item was clearly damaged through misuse.",
  },
  {
    question: "What if I damage an item and need a replacement?",
    answer:
      "Bring the damaged item back to the hardware checkout desk at the QNX Makerspace. If we have extras available, we’ll do our best to replace it for you.",
  },
  {
    question: "Are 3D printers available?",
    answer: (
      <>
        Yes! Our <b>Bambu A1 3D printers</b> will be available on a first-come,
        first-served basis throughout the weekend during hardware checkout
        hours.
        <br />
        <br />
        If you already have a design ready, bring your sliced <b>
          G-code file
        </b>{" "}
        to the lab and one of our volunteers can help you get started. New to 3D
        printing and not sure where to begin? That’s completely fine — we can
        connect you with a hardware mentor who can help you through the process.
      </>
    ),
  },
  {
    question: "How can I give feedback about hardware?",
    answer: (
      <>
        We’d love to hear about your experience with hardware at Hack the North!
        Please fill out our{" "}
        <Link
          href={HARDWARE_FEEDBACK_FORM_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Hardware Feedback Form
        </Link>
        . Your feedback helps us improve the hardware experience and better
        support future projects.
      </>
    ),
  },
];
